import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Search, GraduationCap } from 'lucide-react';

/** Read-only searchable table of a university's per-course catalog
 * (course name, campus, degree level, tuition fee, duration, active
 * intake(s)). Shared between the admin and partner "Universities" tabs. */
export default function ProgramsDialog({ open, onOpenChange, university }) {
  const [search, setSearch] = useState('');
  const programs = university?.programs || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((p) =>
      [p.course_name, p.campus, p.degree_level, p.duration]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [programs, search]);

  const formatFee = (p) => {
    if (p.tuition_fee === null || p.tuition_fee === undefined || p.tuition_fee === '') return '—';
    const n = Number(p.tuition_fee);
    if (Number.isNaN(n)) return String(p.tuition_fee);
    return `${p.tuition_fee_currency || 'AUD'} ${n.toLocaleString()}/yr`;
  };

  // Latvia's source data leaves Active Intake blank for some programs —
  // Latvian universities admit on an Autumn/Winter cycle, so that's the
  // sensible fallback there instead of a bare dash. Scoped to Latvia only
  // so other countries' programs tables are unaffected.
  const isLatvia = (university?.country || '').trim().toLowerCase() === 'latvia';
  const formatIntake = (p) => {
    const joined = (p.active_intake || []).join(', ');
    if (joined) return joined;
    return isLatvia ? 'Autumn/Winter' : '—';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="programs-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }} className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 shrink-0" />
            <span className="truncate">{university?.name} — Programs ({programs.length})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative mb-3 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search course, campus, or degree level..."
            data-testid="programs-search-input"
            className="pl-10 h-10"
          />
        </div>

        <div className="overflow-auto flex-1 border border-contrast/10 rounded-lg">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-surface-2 text-ink-3 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Course Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Campus</th>
                <th className="text-left px-3 py-2.5 font-semibold">Degree Level</th>
                <th className="text-left px-3 py-2.5 font-semibold">Tuition Fee</th>
                <th className="text-left px-3 py-2.5 font-semibold">Duration</th>
                <th className="text-left px-3 py-2.5 font-semibold">Active Intake(s)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={i} className="border-t border-contrast/[0.06] hover:bg-surface-2">
                  <td className="px-3 py-2.5 text-ink">{p.course_name}</td>
                  <td className="px-3 py-2.5 text-ink-2">{p.campus || '—'}</td>
                  <td className="px-3 py-2.5 text-ink-2">{p.degree_level || '—'}</td>
                  <td className="px-3 py-2.5 text-ink-2 whitespace-nowrap">{formatFee(p)}</td>
                  <td className="px-3 py-2.5 text-ink-2 whitespace-nowrap">{p.duration || '1 / 2 year'}</td>
                  <td className="px-3 py-2.5 text-ink-2">{formatIntake(p)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-ink-3">
                    {programs.length === 0 ? 'No program data for this university yet.' : 'No programs match your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
